import { AbstractTagPlugin } from 'plugins/tag/abstract/AbstractTagPlugin';
import { APIService } from 'plugins/tag/plugin_data_service/ts/Application/api/APIService';
import { PlaybackParamsBuilderFeature } from 'plugins/tag/plugin_data_service/ts/Application/features/PlaybackParamsBuilder/PlaybackParamsBuilderFeature';
import { NextItemFeature } from 'plugins/tag/plugin_data_service/ts/Application/features/nextItem/NextItemFeature';
import { UECFeature } from 'plugins/tag/plugin_data_service/ts/Application/features/UEC/UECFeature';
import { UserListsFeature } from 'plugins/tag/plugin_data_service/ts/Application/features/UserLists/UserListsFeature';
import { IDataServiceFeature } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/features/IDataServiceFeature';
import { IDataServiceFeatureContext } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/features/IDataServiceFeatureContext';
import { ITagDataService } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/ITagDataService';
import { ITagDataServiceArgument } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/ITagDataServiceArgument';
import { ITagConfigDataService } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/model/config/ITagConfigDataService';
import { ITagConfigDataServiceConfig } from 'plugins/tag/plugin_data_service/ts/Application/interfaces/model/config/ITagConfigDataServiceConfig';
import { AdapterId } from 'plugins/tag/plugin_data_service/ts/Application/model/enums/AdapterId';
import { PTVUrlBuilder } from 'plugins/tag/plugin_data_service/ts/Application/utilities/PTVUrlBuilder';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag Data Service application class
 * @extends AbstractTagPlugin
 */
export class TagDataService extends AbstractTagPlugin implements ITagDataService {

	/**
	 * @description Deregister function for the previous instance's Conveyor transformer.
	 * Stored as a static so that when a new instance is created (e.g. after tag recreation),
	 * it can clean up the stale transformer left in the global Conveyor registry.
	 * Could be removed after PTV legacy APIs are deprecated
	 * @private
	 */
	static #deregisterPreviousTransformer: ( () => void ) | null = null;

	/**
	 * @description Plugin configuration reference
	 * @private
	 */
	readonly #config: ITagConfigDataService;

	/**
	 * @description Registry of featureKey -> constructor.
	 */
	static readonly #FEATURE_REGISTRY: Record<string, new () => IDataServiceFeature> = {
		PlaybackParamsBuilder: PlaybackParamsBuilderFeature,
		UEC: UECFeature,
		nextItem: NextItemFeature,
		UserLists: UserListsFeature
	};

	/**
	 *  @description Constructor
	 * @param {ITagDataServiceArgument} argument
	 */
	constructor( argument: ITagDataServiceArgument ) {
		super(
			{
				...argument,
				name: 'TagDataService'
			}
		);

		this.#install();
	}

	/**
	 * @description Installs the Data Service into tag.API.DATA: iterates over features defined in config,
	 * instantiates and installs each enabled feature, and registers the combined public API via tag.API.registerPluginAPI().
	 * Individual feature failures are caught and logged without affecting other features.
	 * @private
	 */
	#install = (): void => {
		const config = this.tagConfig?.TagDataService?.params?.config;
		const api = this.tag?.API as { registerPluginAPI?: ( name: string, api: unknown ) => void } | undefined;
		if ( !config?.features || !api?.registerPluginAPI ) {
			this.logger.warn( '[TagDataService] feature install skipped', {
				hasConfigFeatures: !!config?.features,
				hasRegisterPluginAPI: !!api?.registerPluginAPI,
				hasTag: !!this.tag,
				hasTagApi: !!this.tag?.API
			} );
			return;
		}
		const adapterId = config.api?.adapterId ?? AdapterId.PLUTO;
		PTVUrlBuilder.setApiMode( adapterId );
		const apiService = new APIService( { api: config.api } );
		const context: IDataServiceFeatureContext = {
			tag: this.tag,
			utils: this.utils,
			logger: this.logger,
			tagConfig: this.tagConfig,
			apiService
		};
		const dataApi: Record<string, unknown> = {};
		const registry = TagDataService.#FEATURE_REGISTRY;
		for ( const name of Object.keys( config.features ) ) {
			const FeatureCtor = registry[ name ];
			if ( !FeatureCtor ) {
				this.logger?.warn( `[TagDataService] unknown feature in config: "${name}"` );
			} else {
				this.#installFeature( name, FeatureCtor, config, context, dataApi );
			}
		}
		api.registerPluginAPI( 'DATA', dataApi );
	};

	/**
	 * @description Instantiates and installs a single feature, collecting its public API.
	 * Failures are caught and logged without affecting other features.
	 * @private
	 */
	#installFeature = (
		name: string,
		FeatureCtor: new () => IDataServiceFeature,
		config: ITagConfigDataServiceConfig,
		context: IDataServiceFeatureContext,
		dataApi: Record<string, unknown>
	): void => {
		try {
			const feature: IDataServiceFeature = new FeatureCtor();
			if ( feature.enabled( config ) ) {
				feature.install( context );
				dataApi[ name ] = feature.getPublicAPI();
			}
		} catch ( err ) {
			this.logger?.warn( `[TagDataService] failed to install feature: "${name}"`, err );
		}
	};

}